import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Divider,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from "antd";

import {
    BankOutlined,
    DeleteOutlined,
    DollarOutlined,
    DownloadOutlined,
    EditOutlined,
    EyeOutlined,
    FilePdfOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
    WalletOutlined,
} from "@ant-design/icons";

import dayjs from "dayjs";

import {
    PDFDownloadLink,
} from "@react-pdf/renderer";

import api from "../api/api";

import {
    useAcademicYear,
} from "../AcademicYearContext";

import DashboardForExpenditurePDF from "./DashboardForExpenditurePDF";


const {
    Title,
    Text,
} = Typography;

const {
    RangePicker,
} = DatePicker;


const PAGE_SIZE = 10;


const CURRENCY_OPTIONS = [
    {
        value: "LRD",
        label: "Liberian Dollar (LRD)",
    },

    {
        value: "USD",
        label: "United States Dollar (USD)",
    },
];


const CATEGORY_OPTIONS = [
    {
        value: "salary",
        label: "Salary",
    },
    {
        value: "utilities",
        label: "Utilities",
    },
    {
        value: "transportation",
        label: "Transportation",
    },
    {
        value: "maintenance",
        label: "Maintenance",
    },
    {
        value: "stationery",
        label: "Stationery",
    },
    {
        value: "instructional_materials",
        label: "Instructional Materials",
    },
    {
        value: "feeding",
        label: "Feeding",
    },
    {
        value: "medical",
        label: "Medical",
    },
    {
        value: "security",
        label: "Security",
    },
    {
        value: "rent",
        label: "Rent",
    },
    {
        value: "technology",
        label: "Technology",
    },
    {
        value: "events",
        label: "Events",
    },
    {
        value: "tax",
        label: "Tax",
    },
    {
        value: "bank_charges",
        label: "Bank Charges",
    },
    {
        value: "refund",
        label: "Refund",
    },
    {
        value: "miscellaneous",
        label: "Miscellaneous",
    },
    {
        value: "renovation",
        label: "Renovation",
    },
    {
        value: "logistic",
        label: "Logistic",
    },
    {
        value: "service",
        label: "Service",
    },
    {
        value: "wages",
        label: "Wages",
    },
    {
        value: "other",
        label: "Other",
    },
];


const safeNumber = (
    value
) => {

    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : 0;

};


const formatMoney = (
    value
) => {

    return safeNumber(
        value
    ).toLocaleString(
        "en-US",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    );

};


const formatDate = (
    value
) => {

    if (!value) {

        return "-";

    }

    const date =
        dayjs(value);

    if (!date.isValid()) {

        return value;

    }

    return date.format(
        "DD MMM YYYY"
    );

};


const formatCategory = (
    value
) => {

    if (!value) {

        return "Other";

    }

    return String(
        value
    )
        .replaceAll(
            "_",
            " "
        )
        .replace(
            /\b\w/g,
            character =>
                character.toUpperCase()
        );

};


const DashboardForExpenditure = () => {

    const {
        selectedYear,
    } = useAcademicYear();


    const [
        form,
    ] = Form.useForm();


    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        summaryLoading,
        setSummaryLoading,
    ] = useState(false);


    const [
        submitting,
        setSubmitting,
    ] = useState(false);


    const [
        deleteLoading,
        setDeleteLoading,
    ] = useState(false);


    const [
        pdfPreparing,
        setPdfPreparing,
    ] = useState(false);


    const [
        expenditures,
        setExpenditures,
    ] = useState([]);


    const [
        pdfExpenditures,
        setPdfExpenditures,
    ] = useState([]);


    const [
        financialData,
        setFinancialData,
    ] = useState({});


    const [
        totalRecords,
        setTotalRecords,
    ] = useState(0);


    const [
        currentPage,
        setCurrentPage,
    ] = useState(1);


    const [
        search,
        setSearch,
    ] = useState("");


    const [
        filterCurrency,
        setFilterCurrency,
    ] = useState(null);


    const [
        filterCategory,
        setFilterCategory,
    ] = useState(null);


    const [
        dateRange,
        setDateRange,
    ] = useState(null);


    const [
        editingRecord,
        setEditingRecord,
    ] = useState(null);


    const [
        viewingRecord,
        setViewingRecord,
    ] = useState(null);


    const [
        formModalOpen,
        setFormModalOpen,
    ] = useState(false);


    const [
        viewModalOpen,
        setViewModalOpen,
    ] = useState(false);


    const selectedAcademicYearId =
        selectedYear?.id || null;


    /*
     * ==========================================================
     * BUILD EXPENDITURE PARAMETERS
     * ==========================================================
     */

    const buildExpenditureParams =
        useCallback(
            ({
                page = 1,
                pageSize = PAGE_SIZE,
                includeSearch = true,
            } = {}) => {

                const params = {
                    academic_year:
                        selectedAcademicYearId,

                    page,

                    page_size:
                        pageSize,
                };


                if (
                    includeSearch &&
                    search?.trim()
                ) {

                    params.search =
                        search.trim();

                }


                if (
                    filterCurrency
                ) {

                    params.currency =
                        filterCurrency;

                }


                if (
                    filterCategory
                ) {

                    params.category =
                        filterCategory;

                }


                if (
                    dateRange &&
                    dateRange.length === 2
                ) {

                    params.start_date =
                        dateRange[0].format(
                            "YYYY-MM-DD"
                        );

                    params.end_date =
                        dateRange[1].format(
                            "YYYY-MM-DD"
                        );

                }


                return params;

            },
            [
                selectedAcademicYearId,
                search,
                filterCurrency,
                filterCategory,
                dateRange,
            ]
        );


    /*
     * ==========================================================
     * FETCH TABLE EXPENDITURES
     * ==========================================================
     */

    const fetchExpenditures =
        useCallback(
            async ({
                page = 1,
                silent = false,
            } = {}) => {

                if (
                    !selectedAcademicYearId
                ) {

                    setExpenditures([]);

                    setTotalRecords(0);

                    return;

                }


                try {

                    if (!silent) {

                        setLoading(true);

                    }


                    const response =
                        await api.get(
                            "/expenditures/",
                            {
                                params:
                                    buildExpenditureParams(
                                        {
                                            page,
                                            pageSize:
                                                PAGE_SIZE,
                                        }
                                    ),
                            }
                        );


                    const data =
                        response?.data || {};


                    const results =
                        Array.isArray(
                            data
                        )
                            ? data
                            : data?.results || [];


                    setExpenditures(
                        results
                    );


                    setTotalRecords(
                        data?.count ??
                        results.length
                    );


                } catch (error) {

                    console.error(
                        "Failed to fetch expenditures:",
                        error
                    );

                    message.error(
                        "Unable to load expenditure records."
                    );


                } finally {

                    if (!silent) {

                        setLoading(false);

                    }

                }

            },
            [
                selectedAcademicYearId,
                buildExpenditureParams,
            ]
        );


    /*
     * ==========================================================
     * FETCH FINANCIAL SUMMARY
     *
     * CORRECT RESPONSE STRUCTURE:
     *
     * response.data
     *     └── income_statement
     *             ├── tuition
     *             ├── scholarship
     *             ├── ward
     *             ├── total_intake
     *             ├── total_expenditure
     *             └── overall_cash_balance
     * ==========================================================
     */

    const fetchFinancialSummary =
        useCallback(
            async () => {

                if (
                    !selectedAcademicYearId
                ) {

                    setFinancialData({});

                    return;

                }


                try {

                    setSummaryLoading(
                        true
                    );


                    const params = {
                        academic_year:
                            selectedAcademicYearId,
                    };


                    if (
                        dateRange &&
                        dateRange.length === 2
                    ) {

                        params.start_date =
                            dateRange[0].format(
                                "YYYY-MM-DD"
                            );

                        params.end_date =
                            dateRange[1].format(
                                "YYYY-MM-DD"
                            );

                    }


                    const response =
                        await api.get(
                            "/income-statement/",
                            {
                                params,
                            }
                        );


                    const data =
                        response?.data || {};


                    console.log(
                        "FINANCIAL SUMMARY RESPONSE:",
                        data
                    );


                    setFinancialData(
                        data
                    );


                } catch (error) {

                    console.error(
                        "Failed to fetch financial summary:",
                        error
                    );

                    message.error(
                        "Unable to load financial summary."
                    );


                } finally {

                    setSummaryLoading(
                        false
                    );

                }

            },
            [
                selectedAcademicYearId,
                dateRange,
            ]
        );


    /*
     * ==========================================================
     * FETCH ALL EXPENDITURES FOR PDF
     *
     * The table may display only 10 records,
     * but the PDF should contain the full report.
     * ==========================================================
     */

    const fetchAllExpendituresForPDF =
        useCallback(
            async () => {

                if (
                    !selectedAcademicYearId
                ) {

                    setPdfExpenditures([]);

                    return [];

                }


                try {

                    setPdfPreparing(
                        true
                    );


                    let allResults = [];

                    let page = 1;

                    let hasMore =
                        true;


                    while (
                        hasMore
                    ) {

                        const response =
                            await api.get(
                                "/expenditures/",
                                {
                                    params:
                                        buildExpenditureParams(
                                            {
                                                page,
                                                pageSize: 500,
                                            }
                                        ),
                                }
                            );


                        const data =
                            response?.data || {};


                        const results =
                            Array.isArray(
                                data
                            )
                                ? data
                                : data?.results || [];


                        allResults = [
                            ...allResults,
                            ...results,
                        ];


                        const total =
                            data?.count ??
                            allResults.length;


                        if (
                            Array.isArray(
                                data
                            )
                        ) {

                            hasMore =
                                false;

                        } else {

                            hasMore =
                                allResults.length <
                                total;

                        }


                        page += 1;


                        if (
                            results.length === 0
                        ) {

                            hasMore =
                                false;

                        }

                    }


                    setPdfExpenditures(
                        allResults
                    );


                    return allResults;


                } catch (error) {

                    console.error(
                        "Failed to fetch PDF expenditures:",
                        error
                    );

                    message.error(
                        "Unable to prepare expenditure PDF."
                    );

                    return [];


                } finally {

                    setPdfPreparing(
                        false
                    );

                }

            },
            [
                selectedAcademicYearId,
                buildExpenditureParams,
            ]
        );


    /*
     * ==========================================================
     * INITIAL DATA LOAD
     * ==========================================================
     */

    useEffect(() => {

        if (
            !selectedAcademicYearId
        ) {

            setExpenditures([]);

            setFinancialData({});

            setTotalRecords(0);

            return;

        }


        setCurrentPage(
            1
        );


        fetchExpenditures({
            page: 1,
        });


        fetchFinancialSummary();


    }, [
        selectedAcademicYearId,
        dateRange,
        fetchExpenditures,
        fetchFinancialSummary,
    ]);


    /*
     * ==========================================================
     * SEARCH / FILTER DEBOUNCE
     * ==========================================================
     */

    useEffect(() => {

        if (
            !selectedAcademicYearId
        ) {

            return;

        }


        const timer =
            setTimeout(
                () => {

                    setCurrentPage(
                        1
                    );

                    fetchExpenditures({
                        page: 1,
                    });

                },
                350
            );


        return () => {

            clearTimeout(
                timer
            );

        };

    }, [
        search,
        filterCurrency,
        filterCategory,
        selectedAcademicYearId,
        fetchExpenditures,
    ]);


    /*
     * ==========================================================
     * REFRESH ALL DATA
     * ==========================================================
     */

    const refreshData =
        useCallback(
            async () => {

                await Promise.all([
                    fetchExpenditures({
                        page:
                            currentPage,
                    }),

                    fetchFinancialSummary(),
                ]);

            },
            [
                currentPage,
                fetchExpenditures,
                fetchFinancialSummary,
            ]
        );


    /*
     * ==========================================================
     * CREATE MODAL
     * ==========================================================
     */

    const openCreateModal =
        () => {

            if (
                !selectedAcademicYearId
            ) {

                message.warning(
                    "Please select an academic year first."
                );

                return;

            }


            setEditingRecord(
                null
            );


            form.resetFields();


            form.setFieldsValue({
                currency:
                    "LRD",

                category:
                    "other",

                expenditure_date:
                    dayjs(),
            });


            setFormModalOpen(
                true
            );

        };


    /*
     * ==========================================================
     * EDIT MODAL
     * ==========================================================
     */

    const openEditModal =
        (
            record
        ) => {

            setEditingRecord(
                record
            );


            form.setFieldsValue({

                title:
                    record?.title,

                description:
                    record?.description || "",

                category:
                    record?.category,

                amount:
                    safeNumber(
                        record?.amount
                    ),

                currency:
                    record?.currency,

                expenditure_date:
                    record?.expenditure_date
                        ? dayjs(
                            record.expenditure_date
                        )
                        : dayjs(),

                reference_number:
                    record?.reference_number || "",

                receipt_number:
                    record?.receipt_number || "",

            });


            setFormModalOpen(
                true
            );

        };


    /*
     * ==========================================================
     * SUBMIT
     * ==========================================================
     */

    const handleSubmit =
        async () => {

            try {

                const values =
                    await form.validateFields();


                if (
                    !selectedAcademicYearId
                ) {

                    message.error(
                        "A valid academic year is required."
                    );

                    return;

                }


                const payload = {

                    academic_year:
                        selectedAcademicYearId,

                    title:
                        values?.title
                            ?.trim(),

                    description:
                        values?.description
                            ?.trim() || "",

                    category:
                        values?.category,

                    amount:
                        values?.amount,

                    currency:
                        values?.currency,

                    expenditure_date:
                        values?.expenditure_date
                            ? values
                                .expenditure_date
                                .format(
                                    "YYYY-MM-DD"
                                )
                            : dayjs()
                                .format(
                                    "YYYY-MM-DD"
                                ),

                    reference_number:
                        values?.reference_number
                            ?.trim() || "",

                    receipt_number:
                        values?.receipt_number
                            ?.trim() || "",

                };


                setSubmitting(
                    true
                );


                if (
                    editingRecord?.id
                ) {

                    await api.patch(
                        `/expenditures/${editingRecord.id}/`,
                        payload
                    );


                    message.success(
                        "Expenditure updated successfully."
                    );


                } else {

                    await api.post(
                        "/expenditures/",
                        payload
                    );


                    message.success(
                        "Expenditure recorded successfully."
                    );

                }


                setFormModalOpen(
                    false
                );


                setEditingRecord(
                    null
                );


                form.resetFields();


                await refreshData();


            } catch (error) {

                if (
                    error?.errorFields
                ) {

                    return;

                }


                console.error(
                    "Expenditure submission error:",
                    error
                );


                const backendError =
                    error?.response?.data;


                if (
                    backendError &&
                    typeof backendError === "object"
                ) {

                    const firstError =
                        Object.values(
                            backendError
                        )?.[0];


                    if (
                        Array.isArray(
                            firstError
                        )
                    ) {

                        message.error(
                            firstError?.[0] ||
                            "Unable to save expenditure."
                        );

                    } else if (
                        typeof firstError ===
                        "string"
                    ) {

                        message.error(
                            firstError
                        );

                    } else {

                        message.error(
                            "Unable to save expenditure."
                        );

                    }

                } else {

                    message.error(
                        "Unable to save expenditure."
                    );

                }


            } finally {

                setSubmitting(
                    false
                );

            }

        };


    /*
     * ==========================================================
     * DELETE
     * ==========================================================
     */

    const handleDelete =
        async (
            record
        ) => {

            try {

                setDeleteLoading(
                    true
                );


                await api.delete(
                    `/expenditures/${record.id}/`
                );


                message.success(
                    "Expenditure deleted successfully."
                );


                await refreshData();


            } catch (error) {

                console.error(
                    "Delete expenditure error:",
                    error
                );

                message.error(
                    "Unable to delete expenditure."
                );


            } finally {

                setDeleteLoading(
                    false
                );

            }

        };


    /*
     * ==========================================================
     * FINANCIAL VALUES
     *
     * CORRECT PATHS
     * ==========================================================
     */

    const income =
        financialData?.income_statement ||
        {};


    const tuition =
        income?.tuition ||
        {};


    const scholarship =
        income?.scholarship ||
        {};


    const ward =
        income?.ward ||
        {};


    const totalIntake =
        income?.total_intake ||
        {};


    /*
     * IMPORTANT:
     * total_expenditure is inside income_statement.
     */

    const totalExpenditure =
        income?.total_expenditure ||
        {};


    /*
     * IMPORTANT:
     * overall_cash_balance is inside income_statement.
     */

    const cashBalance =
        income?.overall_cash_balance ||
        income?.available_cash ||
        {};


    const lrdIntake =
        safeNumber(
            totalIntake?.lrd
        );


    const usdIntake =
        safeNumber(
            totalIntake?.usd
        );


    const lrdExpenditure =
        safeNumber(
            totalExpenditure?.lrd
        );


    const usdExpenditure =
        safeNumber(
            totalExpenditure?.usd
        );


    const lrdBalance =
        safeNumber(
            cashBalance?.lrd
        );


    const usdBalance =
        safeNumber(
            cashBalance?.usd
        );


    /*
     * ==========================================================
     * PDF FILE NAME
     * ==========================================================
     */

    const pdfFileName =
        useMemo(
            () => {

                const yearName =
                    selectedYear?.name ||
                    "Academic-Year";


                return (
                    `Expenditure-Statement-${yearName}`
                        .replace(
                            /[^a-zA-Z0-9-_]/g,
                            "-"
                        )
                    +
                    ".pdf"
                );

            },
            [
                selectedYear,
            ]
        );


    /*
     * ==========================================================
     * TABLE COLUMNS
     * ==========================================================
     */

    const columns = [

        {
            title: "#",
            key: "index",
            width: 65,
            render: (
                _,
                __,
                index
            ) =>
                (
                    (
                        currentPage - 1
                    )
                    *
                    PAGE_SIZE
                )
                +
                index
                +
                1,
        },

        {
            title: "Date",
            dataIndex:
                "expenditure_date",
            key:
                "expenditure_date",
            width: 125,
            render: (
                value
            ) =>
                formatDate(
                    value
                ),
        },

        {
            title:
                "Expenditure",

            dataIndex:
                "title",

            key:
                "title",

            width:
                240,

            render: (
                value,
                record
            ) => (

                <Space
                    direction="vertical"
                    size={0}
                >

                    <Text strong>
                        {value}
                    </Text>

                    {record?.description && (

                        <Text
                            type="secondary"
                            style={{
                                fontSize:
                                    12,
                            }}
                        >
                            {
                                record.description
                            }
                        </Text>

                    )}

                </Space>

            ),
        },

        {
            title:
                "Category",

            dataIndex:
                "category",

            key:
                "category",

            width:
                150,

            render: (
                value,
                record
            ) => (

                <Tag>
                    {
                        record?.category_display ||
                        formatCategory(
                            value
                        )
                    }
                </Tag>

            ),
        },

        {
            title:
                "Currency",

            dataIndex:
                "currency",

            key:
                "currency",

            width:
                110,

            render: (
                value
            ) => (

                <Tag
                    color={
                        value === "USD"
                            ? "green"
                            : "blue"
                    }
                >
                    {value}
                </Tag>

            ),
        },

        {
            title:
                "Amount",

            dataIndex:
                "amount",

            key:
                "amount",

            width:
                160,

            align:
                "right",

            render: (
                value,
                record
            ) => (

                <Text strong>
                    {
                        record?.currency ===
                        "USD"
                            ? "$ "
                            : "LRD "
                    }

                    {
                        formatMoney(
                            value
                        )
                    }
                </Text>

            ),
        },

        {
            title:
                "Reference",

            key:
                "reference",

            width:
                160,

            render: (
                _,
                record
            ) =>
                record?.reference_number ||
                record?.receipt_number ||
                "-",
        },

        {
            title:
                "Actions",

            key:
                "actions",

            fixed:
                "right",

            width:
                150,

            render: (
                _,
                record
            ) => (

                <Space size={2}>

                    <Tooltip title="View">

                        <Button
                            type="text"
                            icon={
                                <EyeOutlined />
                            }
                            onClick={() => {

                                setViewingRecord(
                                    record
                                );

                                setViewModalOpen(
                                    true
                                );

                            }}
                        />

                    </Tooltip>


                    <Tooltip title="Edit">

                        <Button
                            type="text"
                            icon={
                                <EditOutlined />
                            }
                            onClick={() =>
                                openEditModal(
                                    record
                                )
                            }
                        />

                    </Tooltip>


                    <Popconfirm
                        title="Delete expenditure?"
                        description="This action cannot be undone."
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{
                            danger:
                                true,

                            loading:
                                deleteLoading,
                        }}
                        onConfirm={() =>
                            handleDelete(
                                record
                            )
                        }
                    >

                        <Tooltip title="Delete">

                            <Button
                                danger
                                type="text"
                                icon={
                                    <DeleteOutlined />
                                }
                            />

                        </Tooltip>

                    </Popconfirm>

                </Space>

            ),
        },

    ];


    /*
     * ==========================================================
     * NO ACADEMIC YEAR
     * ==========================================================
     */

    if (
        !selectedYear
    ) {

        return (

            <div
                style={{
                    padding:
                        24,
                }}
            >

                <Card>

                    <Empty
                        image={
                            Empty.PRESENTED_IMAGE_SIMPLE
                        }
                        description={

                            <Space
                                direction="vertical"
                            >

                                <Text strong>
                                    No Academic Year Selected
                                </Text>

                                <Text type="secondary">
                                    Select an academic year before managing expenditures.
                                </Text>

                            </Space>

                        }
                    />

                </Card>

            </div>

        );

    }


    return (

        <div
            style={{
                padding:
                    "20px",

                background:
                    "#f5f7fa",

                minHeight:
                    "100vh",
            }}
        >

            <Card
                bordered={false}
                style={{
                    marginBottom:
                        18,

                    borderRadius:
                        14,

                    boxShadow:
                        "0 2px 14px rgba(0,0,0,0.06)",
                }}
            >

                <Row
                    gutter={[
                        16,
                        16,
                    ]}
                    align="middle"
                    justify="space-between"
                >

                    <Col>

                        <Space
                            direction="vertical"
                            size={4}
                        >

                            <Title
                                level={2}
                                style={{
                                    margin:
                                        0,
                                }}
                            >
                                Expenditure Management
                            </Title>

                            <Text type="secondary">
                                Record, monitor and analyze school expenditures and available cash balances.
                            </Text>

                            <Space wrap>

                                <Tag
                                    color="blue"
                                    style={{
                                        fontWeight:
                                            600,
                                    }}
                                >
                                    Academic Year:{" "}
                                    {
                                        selectedYear.name
                                    }
                                </Tag>

                                {selectedYear.is_active && (

                                    <Tag color="green">
                                        Active Year
                                    </Tag>

                                )}

                            </Space>

                        </Space>

                    </Col>


                    <Col>

                        <Space wrap>

                            <Button
                                icon={
                                    <ReloadOutlined />
                                }
                                loading={
                                    loading ||
                                    summaryLoading
                                }
                                onClick={
                                    refreshData
                                }
                            >
                                Refresh
                            </Button>


                            <Button
                                icon={
                                    <FilePdfOutlined />
                                }
                                loading={
                                    pdfPreparing
                                }
                                onClick={
                                    fetchAllExpendituresForPDF
                                }
                            >
                                Prepare PDF
                            </Button>


                            {pdfExpenditures.length > 0 && (

                                <PDFDownloadLink
                                    document={
                                        <DashboardForExpenditurePDF
                                            financialData={
                                                financialData
                                            }
                                            expenditures={
                                                pdfExpenditures
                                            }
                                            academicYear={
                                                selectedYear
                                            }
                                            generatedAt={
                                                new Date()
                                            }
                                        />
                                    }
                                    fileName={
                                        pdfFileName
                                    }
                                >
                                    {({
                                        loading:
                                        pdfLoading,
                                    }) => (

                                        <Button
                                            type="primary"
                                            icon={
                                                <DownloadOutlined />
                                            }
                                            loading={
                                                pdfLoading
                                            }
                                        >
                                            Download PDF
                                        </Button>

                                    )}
                                </PDFDownloadLink>

                            )}


                            <Button
                                type="primary"
                                icon={
                                    <PlusOutlined />
                                }
                                onClick={
                                    openCreateModal
                                }
                            >
                                New Expenditure
                            </Button>

                        </Space>

                    </Col>

                </Row>

            </Card>


            <Row
                gutter={[
                    16,
                    16,
                ]}
                style={{
                    marginBottom:
                        18,
                }}
            >

                <Col
                    xs={24}
                    sm={12}
                    lg={6}
                >

                    <Card
                        loading={
                            summaryLoading
                        }
                    >

                        <Statistic
                            title="Total LRD Intake"
                            value={
                                lrdIntake
                            }
                            precision={2}
                            prefix="LRD"
                            valueStyle={{
                                fontWeight:
                                    700,
                            }}
                        />

                    </Card>

                </Col>


                <Col
                    xs={24}
                    sm={12}
                    lg={6}
                >

                    <Card
                        loading={
                            summaryLoading
                        }
                    >

                        <Statistic
                            title="Total USD Intake"
                            value={
                                usdIntake
                            }
                            precision={2}
                            prefix="$"
                            valueStyle={{
                                fontWeight:
                                    700,
                            }}
                        />

                    </Card>

                </Col>


                <Col
                    xs={24}
                    sm={12}
                    lg={6}
                >

                    <Card
                        loading={
                            summaryLoading
                        }
                    >

                        <Statistic
                            title="LRD Expenditure"
                            value={
                                lrdExpenditure
                            }
                            precision={2}
                            prefix="LRD"
                            valueStyle={{
                                color:
                                    "#cf1322",

                                fontWeight:
                                    700,
                            }}
                        />

                    </Card>

                </Col>


                <Col
                    xs={24}
                    sm={12}
                    lg={6}
                >

                    <Card
                        loading={
                            summaryLoading
                        }
                    >

                        <Statistic
                            title="USD Expenditure"
                            value={
                                usdExpenditure
                            }
                            precision={2}
                            prefix="$"
                            valueStyle={{
                                color:
                                    "#cf1322",

                                fontWeight:
                                    700,
                            }}
                        />

                    </Card>

                </Col>

            </Row>


            <Card
                bordered={false}
                style={{
                    marginBottom:
                        18,

                    borderRadius:
                        14,
                }}
            >

                <Row
                    gutter={[
                        16,
                        16,
                    ]}
                >

                    <Col
                        xs={24}
                        md={12}
                    >

                        <Card
                            size="small"
                            style={{
                                borderRadius:
                                    12,
                            }}
                        >

                            <Space
                                direction="vertical"
                                size={4}
                            >

                                <Space>

                                    <WalletOutlined />

                                    <Text strong>
                                        Overall LRD Cash Balance
                                    </Text>

                                </Space>

                                <Title
                                    level={2}
                                    style={{
                                        margin:
                                            0,
                                    }}
                                >
                                    LRD{" "}
                                    {
                                        formatMoney(
                                            lrdBalance
                                        )
                                    }
                                </Title>

                                <Text type="secondary">
                                    Total LRD intake less all LRD expenditure.
                                </Text>

                            </Space>

                        </Card>

                    </Col>


                    <Col
                        xs={24}
                        md={12}
                    >

                        <Card
                            size="small"
                            style={{
                                borderRadius:
                                    12,
                            }}
                        >

                            <Space
                                direction="vertical"
                                size={4}
                            >

                                <Space>

                                    <DollarOutlined />

                                    <Text strong>
                                        Overall USD Cash Balance
                                    </Text>

                                </Space>

                                <Title
                                    level={2}
                                    style={{
                                        margin:
                                            0,
                                    }}
                                >
                                    ${" "}
                                    {
                                        formatMoney(
                                            usdBalance
                                        )
                                    }
                                </Title>

                                <Text type="secondary">
                                    Total USD intake less all USD expenditure.
                                </Text>

                            </Space>

                        </Card>

                    </Col>

                </Row>


                <Divider />


                <Alert
                    type="info"
                    showIcon
                    message="Currency Integrity"
                    description={
                        `LRD ${formatMoney(lrdIntake)} - LRD ${formatMoney(lrdExpenditure)} = LRD ${formatMoney(lrdBalance)}. ` +
                        `USD ${formatMoney(usdIntake)} - USD ${formatMoney(usdExpenditure)} = USD ${formatMoney(usdBalance)}. ` +
                        "LRD and USD are maintained as separate cash balances."
                    }
                />

            </Card>


            <Card
                bordered={false}
                style={{
                    borderRadius:
                        14,

                    boxShadow:
                        "0 2px 14px rgba(0,0,0,0.05)",
                }}
                title="Expenditure Records"
                extra={

                    <Space wrap>

                        <Input
                            allowClear
                            prefix={
                                <SearchOutlined />
                            }
                            placeholder="Search expenditure..."
                            value={
                                search
                            }
                            onChange={
                                event =>
                                    setSearch(
                                        event.target.value
                                    )
                            }
                            style={{
                                width:
                                    220,
                            }}
                        />


                        <Select
                            allowClear
                            placeholder="Currency"
                            value={
                                filterCurrency
                            }
                            options={
                                CURRENCY_OPTIONS
                            }
                            onChange={
                                setFilterCurrency
                            }
                            style={{
                                width:
                                    170,
                            }}
                        />


                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            filterOption={(
                                input,
                                option
                            ) => {

                                const searchValue =
                                    String(
                                        input || ""
                                    )
                                        .toLowerCase()
                                        .trim();

                                const label =
                                    String(
                                        option?.label || ""
                                    )
                                        .toLowerCase();

                                const value =
                                    String(
                                        option?.value || ""
                                    )
                                        .toLowerCase();

                                return (
                                    label.includes(
                                        searchValue
                                    ) ||
                                    value.includes(
                                        searchValue
                                    )
                                );

                            }}
                            placeholder="Search Category"
                            value={
                                filterCategory
                            }
                            options={
                                CATEGORY_OPTIONS
                            }
                            onChange={
                                setFilterCategory
                            }
                            style={{
                                width:
                                    180,
                            }}
                        />


                        <RangePicker
                            value={
                                dateRange
                            }
                            onChange={
                                setDateRange
                            }
                        />

                    </Space>

                }
            >

                <Table
                    rowKey="id"
                    columns={
                        columns
                    }
                    dataSource={
                        expenditures
                    }
                    loading={
                        loading
                    }
                    scroll={{
                        x:
                            1250,
                    }}
                    pagination={{
                        current:
                            currentPage,

                        pageSize:
                            PAGE_SIZE,

                        total:
                            totalRecords,

                        showSizeChanger:
                            false,

                        showTotal:
                            total =>
                                `Total ${total} expenditures`,

                        onChange:
                            page => {

                                setCurrentPage(
                                    page
                                );

                                fetchExpenditures({
                                    page,
                                });

                            },
                    }}
                />

            </Card>


            <Modal
                open={
                    formModalOpen
                }
                title={
                    editingRecord
                        ? "Edit Expenditure"
                        : "New Expenditure"
                }
                width={700}
                okText={
                    editingRecord
                        ? "Update Expenditure"
                        : "Save Expenditure"
                }
                cancelText="Cancel"
                confirmLoading={
                    submitting
                }
                onCancel={() => {

                    setFormModalOpen(
                        false
                    );

                    setEditingRecord(
                        null
                    );

                    form.resetFields();

                }}
                onOk={
                    handleSubmit
                }
            >

                <Form
                    form={
                        form
                    }
                    layout="vertical"
                >

                    <Row
                        gutter={16}
                    >

                        <Col span={24}>

                            <Form.Item
                                label="Expenditure Title"
                                name="title"
                                rules={[
                                    {
                                        required:
                                            true,

                                        message:
                                            "Please enter the expenditure title.",
                                    },
                                ]}
                            >

                                <Input
                                    placeholder="Example: Generator Fuel"
                                />

                            </Form.Item>

                        </Col>


                        <Col
                            xs={24}
                            md={12}
                        >

                            <Form.Item
                                label="Category"
                                name="category"
                                rules={[
                                    {
                                        required:
                                            true,

                                        message:
                                            "Please select a category.",
                                    },
                                ]}
                            >

                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    filterOption={(
                                        input,
                                        option
                                    ) => {

                                        const searchValue =
                                            String(
                                                input || ""
                                            )
                                                .toLowerCase()
                                                .trim();

                                        const label =
                                            String(
                                                option?.label || ""
                                            )
                                                .toLowerCase();

                                        const value =
                                            String(
                                                option?.value || ""
                                            )
                                                .toLowerCase();

                                        return (
                                            label.includes(
                                                searchValue
                                            ) ||
                                            value.includes(
                                                searchValue
                                            )
                                        );

                                    }}
                                    options={
                                        CATEGORY_OPTIONS
                                    }
                                    placeholder="Search or select category"
                                />

                            </Form.Item>

                        </Col>


                        <Col
                            xs={24}
                            md={12}
                        >

                            <Form.Item
                                label="Currency"
                                name="currency"
                                rules={[
                                    {
                                        required:
                                            true,

                                        message:
                                            "Please select a currency.",
                                    },
                                ]}
                            >

                                <Select
                                    options={
                                        CURRENCY_OPTIONS
                                    }
                                />

                            </Form.Item>

                        </Col>


                        <Col
                            xs={24}
                            md={12}
                        >

                            <Form.Item
                                label="Amount"
                                name="amount"
                                rules={[
                                    {
                                        required:
                                            true,

                                        message:
                                            "Please enter an amount.",
                                    },
                                ]}
                            >

                                <InputNumber
                                    min={0.01}
                                    precision={2}
                                    style={{
                                        width:
                                            "100%",
                                    }}
                                    placeholder="0.00"
                                />

                            </Form.Item>

                        </Col>


                        <Col
                            xs={24}
                            md={12}
                        >

                            <Form.Item
                                label="Expenditure Date"
                                name="expenditure_date"
                                rules={[
                                    {
                                        required:
                                            true,
                                    },
                                ]}
                            >

                                <DatePicker
                                    style={{
                                        width:
                                            "100%",
                                    }}
                                />

                            </Form.Item>

                        </Col>


                        <Col span={24}>

                            <Form.Item
                                label="Description"
                                name="description"
                            >

                                <Input.TextArea
                                    rows={3}
                                    placeholder="Optional expenditure description..."
                                />

                            </Form.Item>

                        </Col>


                        <Col
                            xs={24}
                            md={12}
                        >

                            <Form.Item
                                label="Reference Number"
                                name="reference_number"
                            >

                                <Input
                                    placeholder="Optional reference number"
                                />

                            </Form.Item>

                        </Col>


                        <Col
                            xs={24}
                            md={12}
                        >

                            <Form.Item
                                label="Receipt Number"
                                name="receipt_number"
                            >

                                <Input
                                    placeholder="Optional receipt number"
                                />

                            </Form.Item>

                        </Col>

                    </Row>

                </Form>

            </Modal>


            <Modal
                open={
                    viewModalOpen
                }
                title="Expenditure Details"
                footer={null}
                onCancel={() => {

                    setViewModalOpen(
                        false
                    );

                    setViewingRecord(
                        null
                    );

                }}
            >

                {viewingRecord && (

                    <Descriptions
                        bordered
                        column={1}
                        size="small"
                    >

                        <Descriptions.Item
                            label="Title"
                        >
                            {
                                viewingRecord.title
                            }
                        </Descriptions.Item>

                        <Descriptions.Item
                            label="Academic Year"
                        >
                            {
                                viewingRecord.academic_year_name ||
                                selectedYear?.name
                            }
                        </Descriptions.Item>

                        <Descriptions.Item
                            label="Category"
                        >
                            {
                                viewingRecord.category_display ||
                                formatCategory(
                                    viewingRecord.category
                                )
                            }
                        </Descriptions.Item>

                        <Descriptions.Item
                            label="Amount"
                        >
                            {
                                viewingRecord.currency ===
                                "USD"
                                    ? "$ "
                                    : "LRD "
                            }

                            {
                                formatMoney(
                                    viewingRecord.amount
                                )
                            }
                        </Descriptions.Item>

                        <Descriptions.Item
                            label="Date"
                        >
                            {
                                formatDate(
                                    viewingRecord.expenditure_date
                                )
                            }
                        </Descriptions.Item>

                        <Descriptions.Item
                            label="Reference"
                        >
                            {
                                viewingRecord.reference_number ||
                                "-"
                            }
                        </Descriptions.Item>

                        <Descriptions.Item
                            label="Receipt"
                        >
                            {
                                viewingRecord.receipt_number ||
                                "-"
                            }
                        </Descriptions.Item>

                        <Descriptions.Item
                            label="Description"
                        >
                            {
                                viewingRecord.description ||
                                "-"
                            }
                        </Descriptions.Item>

                        <Descriptions.Item
                            label="Created By"
                        >
                            {
                                viewingRecord.created_by_name ||
                                "-"
                            }
                        </Descriptions.Item>

                    </Descriptions>

                )}

            </Modal>

        </div>

    );

};


export default DashboardForExpenditure;